import {
  Components,
  ExtraProps,
  Options as MarkdownProps,
  MarkdownHooks,
} from 'react-markdown';
import {
  HTMLAttributes,
  HTMLProps,
  isValidElement,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import styled from 'styled-components';
import {
  U21Typography,
  U21TypographyProps,
} from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  convertStyleObjectToCSSString,
  getDOMProps,
} from 'app/shared/utils/react';
import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import {
  U21Divider,
  U21DividerProps,
} from 'app/shared/u21-ui/components/display/U21Divider';
import { U21ExternalLinkModal } from 'app/shared/u21-ui/components/display/U21ExternalLinkModal';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, {
  defaultSchema,
  Options as RehypeSantizeOptions,
} from 'rehype-sanitize';
import { LibraryContext } from 'app/shared/u21-ui/components/contexts';
import {
  U21Subsection,
  U21SubsectionProps,
} from 'app/shared/u21-ui/components/layout/U21Subsection';
import rehypeKatex from 'rehype-katex';
import remarkMath, { Options as RemarkMathOptions } from 'remark-math';
import rehypeMermaid from 'rehype-mermaid';
import { alpha } from '@mui/system/colorManipulator';

const REMARK_PLUGINS: MarkdownProps['remarkPlugins'] = [
  remarkGfm,
  [
    remarkMath,
    {
      singleDollarTextMath: false,
    } satisfies RemarkMathOptions,
  ],
];

const REHYPE_PLUGINS: MarkdownProps['rehypePlugins'] = [
  rehypeRaw,
  [
    rehypeSanitize,
    {
      ...defaultSchema,
      protocols: {
        ...(defaultSchema.protocols || {}),
        href: ['http', 'https', 'mailto', 'tel'],
        src: ['http', 'https', 'data'],
      },
    } satisfies RehypeSantizeOptions,
  ],
  rehypeKatex,
  rehypeMermaid,
];

export interface U21MarkdownProps extends HTMLProps<HTMLDivElement> {
  children: string;
  components?: Partial<typeof U21_MARKDOWN_COMPONENTS>;
}

export const U21Markdown = (props: U21MarkdownProps) => {
  const { components: componentOverrides, children, ...rest } = props;

  const components = useMemo(
    () => ({
      ...U21_MARKDOWN_COMPONENTS,
      ...componentOverrides,
    }),
    [componentOverrides],
  );

  return (
    <Container {...getDOMProps(rest)}>
      <MarkdownHooks
        components={components}
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
      >
        {children}
      </MarkdownHooks>
    </Container>
  );
};

const Container = styled.div`
  & > *:first-child {
    margin-top: 0;
  }
  & > *:last-child {
    margin-bottom: 0;
  }

  // let's hope we have max 10 mermaid diagrams...
  #mermaid-0,
  #mermaid-1,
  #mermaid-2,
  #mermaid-3,
  #mermaid-4,
  #mermaid-5,
  #mermaid-6,
  #mermaid-7,
  #mermaid-8,
  #mermaid-9,
  #mermaid-10 {
    .cluster {
      rect {
        fill: ${(props) => alpha(props.theme.palette.info.main, 0.12)};
        stroke: ${(props) => props.theme.palette.info.main};
      }
      .nodeLabel {
        .MuiTypography-root {
          font-weight: 700;
        }
      }
    }

    .edgeLabel p {
      background-color: ${(props) => props.theme.palette.background.neutral};
    }

    .flowchart-link {
      stroke: ${(props) => props.theme.palette.grey[500]};
    }

    .labelBkg {
      background-color: ${(props) => props.theme.palette.background.neutral};
    }

    .marker {
      stroke: ${(props) => props.theme.palette.grey[500]};
      fill: ${(props) => props.theme.palette.grey[500]};
    }

    .node circle,
    .node ellipse,
    .node polygon,
    .node rect {
      fill: ${(props) => alpha(props.theme.palette.primary.main, 0.12)};
      stroke: ${(props) => props.theme.palette.primary.main};
    }
  }
`;

const A = (props: HTMLProps<HTMLAnchorElement> & ExtraProps) => {
  const { href, children } = props;
  const { disableExternalLinkModal } = useContext(LibraryContext);
  if (!href) {
    return null;
  }
  if (
    disableExternalLinkModal ||
    // assume it links to app if no protocol is specified
    (!href.startsWith('http://') && !href.startsWith('https://'))
  ) {
    return (
      <LinkButton color="primary" href={href} variant="text">
        <LinkLabel>{children}</LinkLabel>
      </LinkButton>
    );
  }
  return (
    <U21ExternalLinkModal url={href}>
      {({ onClick }) => (
        <LinkButton color="primary" onClick={onClick} variant="text">
          <LinkLabel>{children}</LinkLabel>
        </LinkButton>
      )}
    </U21ExternalLinkModal>
  );
};

type OmitStandardProps<TProps> = Omit<TProps, 'as' | 'ref'>;

const Details = (
  props: HTMLAttributes<HTMLDetailsElement> &
    ExtraProps & { open?: any; title?: any } & Partial<
      OmitStandardProps<U21SubsectionProps>
    >,
) => {
  const { children, node, open, title, ...rest } = props;

  const { contentTitle, contentChildren } = useMemo<{
    contentTitle: ReactNode;
    contentChildren: ReactNode;
  }>(() => {
    // Convert children to array and find the first element that looks like a summary
    const childrenArray: ReactNode[] = Array.isArray(children)
      ? children
      : [children];
    const summaryIndex = childrenArray.findIndex((child) => {
      return isValidElement(child) && child.props.node?.tagName === 'summary';
    });

    if (summaryIndex < 0) {
      return {
        contentTitle: null,
        contentChildren: childrenArray,
      };
    }

    const summaryChild = childrenArray[summaryIndex];
    return {
      contentTitle: isValidElement(summaryChild)
        ? summaryChild.props?.children
        : null,
      contentChildren: childrenArray.slice(summaryIndex + 1),
    };
  }, [children]);

  return (
    <StyledU21Subsection
      collapsible
      // explicit true check for open since it can be anything
      collapsed={open !== true}
      disableTitleCase
      title={contentTitle || title || 'Click to expand'}
      {...rest}
    >
      {contentChildren}
    </StyledU21Subsection>
  );
};

type TypographyMarkdownProps<TElement extends HTMLElement> =
  HTMLAttributes<TElement> &
    ExtraProps &
    Partial<OmitStandardProps<U21TypographyProps>>;

export const U21_MARKDOWN_COMPONENTS = {
  a: A,
  h1: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H1 variant="h4" {...rest} />;
  },
  h2: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H2 variant="h5" {...rest} />;
  },
  h3: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H3 variant="h6" {...rest} />;
  },
  h4: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H456 variant="subtitle1" {...rest} />;
  },
  h5: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H456 variant="subtitle1" {...rest} />;
  },
  h6: (props: TypographyMarkdownProps<HTMLHeadingElement>) => {
    const { node, ...rest } = props;
    return <H456 variant="subtitle1" {...rest} />;
  },
  p: (props: TypographyMarkdownProps<HTMLParagraphElement>) => {
    const { node, children, ...rest } = props;
    return (
      <P
        component={typeof children === 'string' ? undefined : 'div'}
        variant="body2"
        {...rest}
      >
        {children}
      </P>
    );
  },
  code: (props: TypographyMarkdownProps<HTMLElement>) => {
    const { node, ...rest } = props;
    return <Code inline variant="inherit" {...rest} />;
  },
  pre: (props: TypographyMarkdownProps<HTMLPreElement>) => {
    const { node, ...rest } = props;
    return <Pre variant="body2" {...rest} />;
  },
  hr: (
    props: HTMLAttributes<HTMLHRElement> &
      ExtraProps &
      Partial<OmitStandardProps<U21DividerProps>>,
  ) => {
    const { node, ...rest } = props;
    return <HR horizontal {...rest} />;
  },
  ul: (props: HTMLAttributes<HTMLUListElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return <UL {...rest} />;
  },
  ol: (props: HTMLAttributes<HTMLUListElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return <OL {...rest} />;
  },
  li: (props: HTMLAttributes<HTMLLIElement> & ExtraProps) => {
    const { children, node, ...rest } = props;
    return (
      <li {...getDOMProps(rest)}>
        <LiTypography
          component={typeof children === 'string' ? undefined : 'div'}
          variant="body2"
        >
          {children}
        </LiTypography>
      </li>
    );
  },
  table: (props: HTMLAttributes<HTMLTableElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return (
      <TableWrapper>
        <Table {...rest} />
      </TableWrapper>
    );
  },
  tr: (props: HTMLAttributes<HTMLTableRowElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return <TR {...rest} />;
  },
  th: (props: HTMLAttributes<HTMLTableCellElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return <TH {...rest} />;
  },
  td: (props: HTMLAttributes<HTMLTableCellElement> & ExtraProps) => {
    const { node, ...rest } = props;
    return <TD {...rest} />;
  },
  details: Details,
  // This component is handled within the Details component
  // We return null here as the summary is rendered in Details
  summary: () => null,
} as const satisfies Components;

const H1 = styled(U21Typography)`
  margin-bottom: 16px;
`;

const H2 = styled(U21Typography)`
  margin-top: 16px;
  margin-bottom: 12px;
`;

const H3 = styled(U21Typography)`
  margin-top: 12px;
  margin-bottom: 8px;
`;

const H456 = styled(U21Typography)`
  margin-top: 12px;
  margin-bottom: 8px;
`;

const Code = styled(U21Typography)`
  background-color: ${(props) => props.theme.palette.background.neutral};
  border-radius: 4px;
  padding: 0 4px;
  white-space: pre;

  /* make inline code blocks (single backtick) wrap */
  .MuiTypography-root {
    white-space: break-spaces;
  }
`;

const Pre = styled(U21Typography)`
  background-color: ${(props) => props.theme.palette.background.neutral};
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  overflow: auto;

  /* make fenced code blocks (triple backticks) not wrap */
  ${Code} {
    .MuiTypography-root {
      white-space: unset;
    }
  }
`;

const P = styled(U21Typography)`
  margin-bottom: 16px;

  .MuiTypography-root {
    width: 100%;
  }
`;

const LiTypography = styled(U21Typography)`
  .MuiTypography-root {
    width: 100%;
  }
`;

const HR = styled(U21Divider)`
  margin: 16px 0;
`;

const UL = styled.ul`
  padding-left: 24px;
  margin-bottom: 16px;

  li + li {
    margin-top: 4px;
  }

  // nested lists should not have margin bottom
  ol,
  ul {
    margin-bottom: 0;
  }
`;

const OL = styled.ol`
  padding-left: 24px;
  margin-bottom: 16px;

  li + li {
    margin-top: 4px;
  }

  // nested lists should not have margin bottom
  ol,
  ul {
    margin-bottom: 0;
  }
`;

const TableWrapper = styled.div`
  margin-bottom: 16px;
  overflow: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  border: 1px solid ${(props) => props.theme.palette.divider};
`;

const TR = styled.tr`
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`;

const TD = styled.td`
  padding: 6px 8px;
  ${(props) => convertStyleObjectToCSSString(props.theme.typography.body2)};
`;

const TH = styled.th`
  padding: 6px 8px;
  ${(props) => convertStyleObjectToCSSString(props.theme.typography.subtitle2)};
`;

const LinkButton = styled(U21Button)`
  max-width: 100%;
`;

// text-overflow: ellipsis doesn't work on the inline-flex button directly,
// and using inline-block on the button breaks vertical alignment with surrounding text.
// Wrapping children in a block-level span allows ellipsis to work within the flex container.
const LinkLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledU21Subsection = styled(U21Subsection)`
  margin-bottom: 16px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  width: 100%;
`;
